/**
 * Convert any photo value returned by the backend into a renderable `<img src>`.
 *
 * The backend ships photos in any of these shapes (legacy + new co-exist
 * during the lazy-migration window):
 *   - `null` / `undefined`
 *   - `"data:image/jpeg;base64,..."`               (legacy, kept as-is)
 *   - `"iVBORw0KGgo..."` bare base64                (wrap in data-url)
 *   - `"https://.../foo.jpg"` absolute URL          (returned as-is)
 *   - `"/api/files/taillink/animals/.../uuid.jpg"`  (prefix with backend URL)
 *   - `{ thumb, medium, full }` storage-path dict   (pick variant, prefix)
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function prefix(pathish) {
  if (!pathish) return null;
  pathish = pathish.replace(/^(\/?api\/files\/)+/, '/api/files/');
  if (pathish.startsWith('http://') || pathish.startsWith('https://') || pathish.startsWith('data:')) {
    return pathish;
  }
  if (pathish.startsWith('api/')) {
    return `${BACKEND_URL}/${pathish}`;
  }
  if (pathish.startsWith('/api/')) {
    return `${BACKEND_URL}${pathish}`;
  }
  // bare storage path
  return `${BACKEND_URL}/api/files/${pathish.replace(/^\/+/, '')}`;
}

export function resolvePhotoSrc(photo, variant = 'medium') {
  if (photo == null) return null;
  // Storage-path dict { thumb, medium, full }
  if (typeof photo === 'object') {
    const path = photo[variant] || photo.medium || photo.full || photo.thumb;
    return prefix(path);
  }
  if (typeof photo !== 'string') return null;
  // data-url / http(s) URL / api-path
  if (photo.startsWith('data:') || photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  if (photo.startsWith('/api/') || photo.startsWith('api/')) return prefix(photo);
  // Common base64-encoded image prefixes (JPEG /9j/, PNG iVBOR, GIF R0lGOD,
  // WebP UklGR). These come from legacy DB rows where the data-URL prefix
  // was stripped before storage — wrap them so <img> can render.
  if (photo.startsWith('/9j/') || photo.startsWith('iVBOR') ||
      photo.startsWith('R0lGOD') || photo.startsWith('UklGR')) {
    return `data:image/jpeg;base64,${photo}`;
  }
  // Storage path heuristic: "taillink/animals/{id}/uuid-medium.jpg"
  if (photo.startsWith('taillink/') || photo.includes('/animals/') ||
      photo.includes('-medium.jpg') || photo.includes('-thumb.jpg') ||
      photo.includes('-full.jpg')) {
    return prefix(photo);
  }
  // Long base-64 blob without recognised prefix → wrap as JPEG.
  if (photo.length > 200 && !photo.includes(' ')) {
    return `data:image/jpeg;base64,${photo}`;
  }
  return photo;
}

export default resolvePhotoSrc;
